import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import { useAuth } from '../context/AuthContext';
// ✅ MODIFICACIÓN: Se importa TicketPriority
import { TicketData, Comment as TicketComment, TicketStatus, User, Attachment, TicketPriority } from '../types';
import { ticketStatusTranslations, ticketPriorityTranslations } from '../utils/traslations';
import { formatLocalDate } from '../utils/dateFormatter';
import { getImageUrl } from '../utils/imageUrl';
import CommentForm from '../components/Common/CommentForm';
import { submitTicketComment } from '../utils/submitTicketComment';
import SectionCard from '../components/Common/SectionCard';
import ActivationPlanillaCard from '../components/Tickets/ActivationPlanillaCard';

// ✅ AÑADIDO: Icono de Archivo Genérico
const FileIcon: React.FC<{ className?: string }> = ({ className = "w-16 h-16" }) => (
    <svg className={`${className} mx-auto text-gray-400 mb-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
    </svg>
);

// Componente Badge local para simplicidad visual.
const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${color}`}>
        {children}
    </span>
);

const AgentTicketDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isViewer = user?.role === 'viewer';
    /** Solo admin y agente pueden cambiar estado desde el detalle (mismo endpoint que el listado). */
    const canChangeTicketStatus = user?.role === 'admin' || user?.role === 'agent';

    const [ticket, setTicket] = useState<TicketData | null>(null);
    const [agents, setAgents] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);

    // Carga todos los datos necesarios para la página (ticket y agentes) en una sola llamada.
    const fetchAllData = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [ticketResponse, agentsResponse] = await Promise.all([
                api.get(`/api/tickets/${id}`),
                api.get('/api/users/agents')
            ]);
            setTicket(ticketResponse.data.data);
            setAgents(agentsResponse.data.data || []);
        } catch (error) {
            toast.error("Error al cargar los datos de la página.");
            console.error("Fetch All Data Error:", error);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Maneja la confirmación para reasignar el ticket a un nuevo agente.
    const handleConfirmReassign = async () => {
        if (!selectedAgentId || !ticket) {
            toast.warn("Por favor, selecciona un agente.");
            return;
        }
        try {
            await api.put(`/api/tickets/${ticket.id}/reassign`, { newAgentId: selectedAgentId });
            toast.success("¡Ticket reasignado exitosamente!");
            setSelectedAgentId('');
            await fetchAllData(); 
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al reasignar el ticket.");
        } finally {
            setIsReassignModalOpen(false);
        }
    };

    // Maneja el envío de un nuevo comentario.
    const handleAddComment = async (commentText: string, isInternal: boolean, files?: File[]) => {
        if ((!commentText.trim() && (!files || files.length === 0)) || !ticket) return;
        try {
            await submitTicketComment(ticket.id, commentText, isInternal, files);
            toast.success(files?.length ? 'Mensaje y adjuntos enviados.' : (isInternal ? 'Nota interna añadida.' : 'Comentario añadido.'));
            await fetchAllData();
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(msg || 'Error al añadir comentario.');
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        if (!ticket) return;
        if (!window.confirm('¿Eliminar este mensaje del ticket?')) return;
        try {
            await api.delete(`/api/comments/${commentId}`);
            toast.success('Comentario eliminado.');
            await fetchAllData();
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(msg || 'No se pudo eliminar el comentario.');
        }
    };

    // ✅ AÑADIDO: Función para cambiar la prioridad (para Agente)
    const handlePriorityChange = async (newPriority: TicketPriority) => {
        if (!ticket) return;
        try {
            await api.put(`/api/tickets/${ticket.id}`, { priority: newPriority });
            toast.success(`La prioridad del ticket se actualizó a "${ticketPriorityTranslations[newPriority] || newPriority}".`);
            fetchAllData(); // Recargamos los datos del ticket
        } catch (error) {
            toast.error("No se pudo actualizar la prioridad del ticket.");
        }
    };

    const handleStatusChange = async (newStatus: TicketStatus) => {
        if (!ticket || newStatus === ticket.status) return;
        setStatusSaving(true);
        try {
            await api.put(`/api/tickets/${ticket.id}/status`, { status: newStatus });
            toast.success(`El estado del ticket se actualizó a "${ticketStatusTranslations[newStatus] || newStatus}".`);
            await fetchAllData();
        } catch (error: unknown) {
            const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(msg || 'No se pudo actualizar el estado del ticket.');
        } finally {
            setStatusSaving(false);
        }
    };
    // FIN MODIFICACIÓN
    
    // Funciones auxiliares para estilos...
    const getStatusBadgeColor = (status: TicketStatus) => {
        switch (status) {
            case 'open': return 'bg-blue-100 text-blue-800';
            case 'in-progress': return 'bg-yellow-100 text-yellow-800';
            case 'resolved': return 'bg-green-100 text-green-800';
            case 'closed': return 'bg-gray-200 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) return <div className="p-8 text-center text-lg">Cargando detalles del ticket...</div>;
    if (!ticket) return <div className="p-8 text-center text-lg">Ticket no encontrado.</div>;

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Ticket #{ticket.id}: {ticket.title}</h1>
                <button onClick={() => navigate(-1)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg">Volver</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Sección de Detalles del Ticket */}
                    <SectionCard title="Detalles del Ticket">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <strong className="block text-sm text-gray-500">Cliente</strong>
                                <p className="text-lg">{ticket.client_full_name || ticket.client_name}</p>
                            </div>
                            <div>
                                <strong className="block text-sm text-gray-500">Departamento</strong>
                                <p className="text-lg">{ticket.ticket_department_name || ticket.department_id}</p>
                            </div>
                            <div>
                                <strong className="block text-sm text-gray-500">Creado</strong>
                                <p className="text-lg">{formatLocalDate(ticket.created_at)}</p>
                            </div>
                            <div className="sm:col-span-2">
                                <strong className="block text-sm text-gray-500">Descripción</strong>
                                <p className="whitespace-pre-wrap mt-1 p-3 bg-gray-50 rounded-md border">{ticket.description}</p>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Contacto del cliente — visible para agente/supervisor */}
                    <SectionCard title="Contacto del Cliente">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            {ticket.business_name && (
                                <div className="sm:col-span-2">
                                    <strong className="block text-gray-500">Empresa</strong>
                                    <span>{ticket.business_name}</span>
                                </div>
                            )}
                            <div>
                                <strong className="block text-gray-500">Email</strong>
                                {ticket.client_email ? (
                                    <a href={`mailto:${ticket.client_email}`} className="text-blue-600 hover:underline break-all">
                                        {ticket.client_email}
                                    </a>
                                ) : <span className="text-gray-400">No registrado</span>}
                            </div>
                            <div>
                                <strong className="block text-gray-500">Teléfono / WhatsApp</strong>
                                {ticket.client_phone ? (
                                    <a
                                        href={`https://wa.me/${String(ticket.client_phone).replace(/[^0-9]/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-green-600 hover:underline"
                                    >
                                        {ticket.client_phone}
                                    </a>
                                ) : <span className="text-gray-400">No registrado</span>}
                            </div>
                        </div>
                    </SectionCard>

                    {ticket.activation_data && (
                        <ActivationPlanillaCard activationData={ticket.activation_data} />
                    )}
                    
                    {/* Sección de Archivos Adjuntos */}
                    {ticket.attachments && ticket.attachments.length > 0 && (
                        <SectionCard title="Archivos Adjuntos">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {ticket.attachments.map(att => {
                                    if (!att) return null;
                                    const rawPath = (att as { file_path?: string; file_url?: string }).file_path ?? (att as { file_path?: string; file_url?: string }).file_url ?? '';
                                    if (rawPath == null || rawPath === '') return null;
                                    const fileUrl = getImageUrl(rawPath);
                                    const isImage = att.file_type?.startsWith('image/');
                                    const isVideo = att.file_type?.startsWith('video/');
                                    return (
                                    <a 
                                        key={att.id}
                                        href={fileUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="border rounded-lg p-2 text-center hover:bg-gray-50 transition-colors group block"
                                        title={`Ver: ${att.file_name}`}
                                    >
                                        {isImage ? (
                                            <img 
                                                src={fileUrl} 
                                                alt="" 
                                                className="w-full h-24 object-cover rounded-md mb-2"
                                                onError={(e) => {
                                                    const img = e.currentTarget;
                                                    img.style.display = 'none';
                                                    const placeholder = img.nextElementSibling as HTMLElement;
                                                    if (placeholder) placeholder.classList.remove('hidden');
                                                }}
                                            />
                                        ) : null}
                                        {isImage && (
                                            <div className="hidden w-full h-24 bg-gray-100 rounded-md mb-2 flex items-center justify-center">
                                                <FileIcon className="w-10 h-10 shrink-0" />
                                            </div>
                                        )}
                                        {isVideo ? (
                                            <div className="w-full h-24 bg-black rounded-md mb-2 flex items-center justify-center">
                                                <svg className="w-10 h-10 text-white opacity-75" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path></svg>
                                            </div>
                                        ) : !isImage ? (
                                            <FileIcon className="w-full h-24" />
                                        ) : null}
                                        <p className="text-sm text-gray-700 truncate group-hover:underline">
                                            {att.file_name}
                                        </p>
                                    </a>
                                    );
                                })}
                            </div>
                        </SectionCard>
                    )}
                    
                    {/* Sección de Conversación */}
                    <SectionCard title="Conversación">
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                            {ticket.comments && ticket.comments.length > 0 ? (
                                ticket.comments.map(comment => (
                                    <div key={comment.id} className={`p-3 rounded-lg shadow-sm border ${comment.is_internal ? 'bg-yellow-50 border-yellow-200' : 'bg-white'}`}>
                                        <div className="flex justify-between items-start gap-2 mb-1">
                                            <p className="text-sm font-semibold text-gray-800">{comment.username}</p>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {comment.is_internal ? (
                                                    <span className="text-xs font-bold bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">NOTA INTERNA</span>
                                                ) : null}
                                                {user?.role === 'admin' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                        className="text-xs text-red-600 hover:text-red-800 font-semibold"
                                                        title="Eliminar mensaje"
                                                    >
                                                        Eliminar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.comment_text}</p>
                                        <p className="text-xs text-gray-500 text-right mt-2">{formatLocalDate(comment.created_at)}</p>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500 py-8">No hay comentarios aún.</p>
                            )}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t">
                            {user && !isViewer && <CommentForm onAddComment={handleAddComment} userRole={user.role} />}
                        </div>
                    </SectionCard>
                </div>

                {/* Columna Lateral de Acciones */}
                <div className="lg:col-span-1 space-y-6">
                    <SectionCard title="Estado y Prioridad">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="ticket-status-select" className="block text-sm font-medium text-gray-700">Estado</label>
                                {canChangeTicketStatus ? (
                                    <select
                                        id="ticket-status-select"
                                        value={ticket.status}
                                        onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                                        disabled={ticket.status === 'closed' || statusSaving}
                                        aria-busy={statusSaving}
                                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {Object.entries(ticketStatusTranslations).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="mt-1">
                                        <Badge color={getStatusBadgeColor(ticket.status)}>
                                            {ticketStatusTranslations[ticket.status] || ticket.status}
                                        </Badge>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label htmlFor="priority-select" className="block text-sm font-medium text-gray-700">Prioridad</label>
                                {isViewer ? (
                                    <p className="mt-1 text-gray-800">{ticketPriorityTranslations[ticket.priority] || ticket.priority}</p>
                                ) : (
                                    <select 
                                        id="priority-select"
                                        value={ticket.priority}
                                        onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
                                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                                        disabled={ticket.status === 'closed'}
                                    >
                                        {Object.entries(ticketPriorityTranslations).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                    </SectionCard>
                    
                    <SectionCard title="Agente Asignado">
                        <p className="text-lg font-medium">{ticket.agent_name || 'No asignado'}</p>
                    </SectionCard>
                    {(user?.role === 'admin' || user?.role === 'agent' || user?.role === 'supervisor') && (
                        <SectionCard title="Reasignar Ticket">
                            <div className="flex flex-col gap-3">
                                <select 
                                    value={selectedAgentId} 
                                    onChange={(e) => setSelectedAgentId(e.target.value)} 
                                    className="w-full p-2 border rounded-md bg-white"
                                >
                                    <option value="">-- Selecciona un agente --</option>
                                    {(user?.role === 'admin'
                                        ? agents
                                        : agents.filter((a: User) => a.role === 'agent' || a.role === 'supervisor')
                                    ).map((agent: User) => (
                                        <option key={agent.id} value={agent.id}>
                                            {(agent as User & { full_name?: string }).full_name || agent.username}
                                        </option>
                                    ))}
                                </select>
                                <button 
                                    onClick={() => setIsReassignModalOpen(true)} 
                                    disabled={!selectedAgentId} 
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-gray-400"
                                >
                                    Reasignar
                                </button>
                            </div>
                        </SectionCard>
                    )}
                </div>
            </div>

            {/* Modal de Confirmación para Reasignar */}
            {isReassignModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl">
                        <h3 className="text-lg font-bold">Confirmar Reasignación</h3>
                        <p className="my-4">¿Estás seguro de que quieres reasignar este ticket?</p>
                        <div className="flex justify-end gap-4">
                            <button onClick={() => setIsReassignModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg">Cancelar</button>
                            <button onClick={handleConfirmReassign} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg">Confirmar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentTicketDetailPage;

